/*
  FWUSA carousels, built on Dawn's slider classes in assets/global.js.

  <fw-hero-slideshow>  extends Dawn's SlideshowComponent (sections/fw-hero-carousel.liquid).
    Dawn keeps one interval running, so a slide picked by hand could advance early. Here every
    slide change restarts the timer and the active dot's progress bar together. Hover or focus
    pauses both, and resuming carries on from where they stopped instead of starting the slide's
    time again. Visitors who prefer reduced motion start paused; the play button resumes.

  [data-fw-progress]   a thin bar under a <slider-component> row (sections/fw-model-grid.liquid)
    that shows how far the row has been scrolled and how much of it is in view.

  [data-fw-drag]       lets a mouse drag the hero and the helicopter row sideways. Touch screens, pens and touchpads already
    scroll it natively (scroll snap), so only the mouse is handled here. Snapping is paused while
    dragging, then the row settles on the nearest card, or the next one after a deliberate flick.
    A drag never counts as a click on the card underneath.
*/
(function () {
  "use strict";

  // Dawn declares its slider classes with `class` in a classic script, so they are global bindings.
  if (typeof SlideshowComponent !== "undefined" && !customElements.get("fw-hero-slideshow")) {
    customElements.define("fw-hero-slideshow", class FwHeroSlideshow extends SlideshowComponent {
      setAutoPlay() {
        // Without a pause button Dawn reads this.reducedMotion, which it only sets for the
        // announcement bar.
        this.reducedMotion = this.reducedMotion || window.matchMedia("(prefers-reduced-motion: reduce)");
        super.setAutoPlay();
        var reduced = this.reducedMotion.matches;
        if (this.sliderAutoplayButton) {
          if (reduced) this.autoPlayToggle();
        } else {
          // No pause button: autoplay runs unless the visitor prefers reduced motion, and
          // hover, focus and touch hold it (focusInHandling / focusOutHandling below).
          this.autoplayButtonIsSetToPlay = !reduced;
          if (reduced) this.pause();
        }
        // Touch has no hover, so hold autoplay while a finger is on the slides; otherwise the
        // timer can move the slide mid swipe. It resumes shortly after the finger lifts,
        // unless autoplay is off (pause button or reduced motion).
        var self = this;
        this.slider.addEventListener("touchstart", function () {
          window.clearTimeout(self.touchTimer);
          if (self.autoplayButtonIsSetToPlay) self.pause();
        }, { passive: true });
        this.slider.addEventListener("touchend", function () {
          window.clearTimeout(self.touchTimer);
          self.touchTimer = window.setTimeout(function () {
            if (self.autoplayButtonIsSetToPlay) self.play();
          }, 1500);
        }, { passive: true });
      }

      // Dawn's versions only handle a pause button or the announcement bar; without the button
      // they would throw on hover, so hold autoplay on hover and focus here.
      focusInHandling(event) {
        if (this.sliderAutoplayButton) return super.focusInHandling(event);
        if (this.autoplayButtonIsSetToPlay) this.pause();
      }

      focusOutHandling(event) {
        if (this.sliderAutoplayButton) return super.focusOutHandling(event);
        if (this.autoplayButtonIsSetToPlay) this.play();
      }

      update() {
        super.update();
        if (this.currentPage === this.shownPage) return;
        this.shownPage = this.currentPage;
        // A new slide, picked by hand or by the timer, gets its full time on screen.
        this.elapsed = 0;
        this.restartProgress();
        if (this.running) this.schedule();
      }

      // Time the current slide until it moves on, minus what it has already been shown.
      schedule() {
        window.clearTimeout(this.autoplay);
        this.startedAt = Date.now();
        var self = this;
        this.autoplay = window.setTimeout(function () {
          self.elapsed = 0;
          self.autoRotateSlides();
        }, Math.max(0, this.autoplaySpeed - (this.elapsed || 0)));
      }

      play() {
        this.classList.remove("is-paused");
        // Dawn calls play() again on every hover of the play button; keep the running timer.
        if (this.running) return;
        this.running = true;
        this.slider.setAttribute("aria-live", "off");
        this.schedule();
      }

      pause() {
        this.classList.add("is-paused");
        if (!this.running) return;
        this.running = false;
        this.slider.setAttribute("aria-live", "polite");
        window.clearTimeout(this.autoplay);
        this.elapsed = (this.elapsed || 0) + (Date.now() - this.startedAt);
      }

      restartProgress() {
        this.querySelectorAll(".fw-hero__fill").forEach(function (fill) {
          fill.style.animation = "none";
          void fill.offsetWidth; // reflow so the animation starts again from 0
          fill.style.animation = "";
        });
      }
    });
  }

  function initProgress(scope) {
    scope.querySelectorAll("[data-fw-progress]").forEach(function (bar) {
      if (bar.dataset.ready) return;
      var slider = document.getElementById(bar.dataset.fwProgress);
      var thumb = bar.querySelector("span");
      if (!slider || !thumb) return;
      bar.dataset.ready = "true";
      var draw = function () {
        var max = slider.scrollWidth - slider.clientWidth;
        var seen = slider.scrollWidth ? slider.clientWidth / slider.scrollWidth : 1;
        var done = max > 0 ? slider.scrollLeft / max : 0;
        thumb.style.width = (seen * 100).toFixed(2) + "%";
        thumb.style.transform = "translateX(" + (done * (1 / seen - 1) * 100).toFixed(2) + "%)";
        bar.hidden = max <= 1;
      };
      slider.addEventListener("scroll", draw, { passive: true });
      new ResizeObserver(draw).observe(slider);
      draw();
    });
  }

  var DRAG_THRESHOLD = 6;   // px before a press becomes a drag instead of a click
  var FLICK_DISTANCE = 48;  // px dragged that moves on by one card even if it is nearer the start

  function initDrag(scope) {
    scope.querySelectorAll("[data-fw-drag]").forEach(function (track) {
      if (track.dataset.dragReady) return;
      track.dataset.dragReady = "true";
      var startX = 0, startScroll = 0, moved = 0, pointerId = null, settleTimer;

      function cardStarts() {
        var first = track.firstElementChild;
        if (!first) return [0];
        return Array.prototype.map.call(track.children, function (item) { return item.offsetLeft - first.offsetLeft; });
      }

      function settle(direction) {
        var max = track.scrollWidth - track.clientWidth;
        var starts = cardStarts().filter(function (x) { return x <= max; }).concat([max]);
        var here = track.scrollLeft, target = starts[0];
        starts.forEach(function (x) { if (Math.abs(x - here) < Math.abs(target - here)) target = x; });
        if (direction !== 0 && Math.abs(moved) >= FLICK_DISTANCE) {
          var ahead = starts.filter(function (x) { return direction > 0 ? x > here + 1 : x < here - 1; });
          if (ahead.length) target = direction > 0 ? Math.min.apply(null, ahead) : Math.max.apply(null, ahead);
        }
        track.scrollTo({ left: target, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
        // Give snapping back once the smooth scroll has landed.
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(function () { track.classList.remove("is-settling"); }, 450);
      }

      track.addEventListener("pointerdown", function (event) {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        if (event.target.closest("button, input, select, textarea, label")) return;
        window.clearTimeout(settleTimer);
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        moved = 0;
      });

      track.addEventListener("pointermove", function (event) {
        if (event.pointerId !== pointerId) return;
        moved = event.clientX - startX;
        if (!track.classList.contains("is-dragging")) {
          if (Math.abs(moved) < DRAG_THRESHOLD) return;
          track.classList.add("is-dragging", "is-settling");
          track.setPointerCapture(pointerId);
        }
        track.scrollLeft = startScroll - moved;
        event.preventDefault();
      });

      function end(event) {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!track.classList.contains("is-dragging")) return;
        track.classList.remove("is-dragging");
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        settle(moved < 0 ? 1 : moved > 0 ? -1 : 0);
        // Swallow the click that follows a drag so the card under the cursor does not open.
        // The browser fires that click straight after pointerup, so drop the guard right after;
        // otherwise a drag released outside the row would eat the visitor's next real click.
        var swallow = function (click) {
          click.preventDefault();
          click.stopPropagation();
        };
        track.addEventListener("click", swallow, true);
        window.setTimeout(function () {
          track.removeEventListener("click", swallow, true);
          moved = 0;
        }, 0);
      }
      track.addEventListener("pointerup", end);
      track.addEventListener("pointercancel", end);

      // Stop the browser dragging out the card image or link as a file.
      track.addEventListener("dragstart", function (event) { event.preventDefault(); });
    });
  }

  initProgress(document);
  initDrag(document);
  document.addEventListener("shopify:section:load", function (event) {
    initProgress(event.target);
    initDrag(event.target);
  });
})();
